import { Box, Container, makeStyles } from '@material-ui/core';
import ScheduleIcon from '@material-ui/icons/Schedule';
import React from 'react';
import AxleAccordionContainer from '../AxleAccordionContainer';
import ClinicianTimeSelectionContainer from './ClinicianTimeSelectionContainer';

const useStyles = makeStyles(theme => ({
    clinicianCardWrapper: {
        paddingTop: '24px',
        minWidth: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '16px',
    },
    clinicianCard: {
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        textAlign: 'center',
        width: '100%',
        gap: '16px',
        minHeight: '150px',
        border: '2px solid #b9b9b7ff',
    },
    clinicianCardContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'center',
        width: '100%',
        gap: '16px',
    },
    clinicianIcon: {
        borderRadius: '50%',
        width: '75px',
        minWidth: '75px',
        height: '75px',
        overflow: 'hidden',
        backgroundColor: '#E8E6E1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        [theme.breakpoints.down('sm')]: {
            width: '50px',
            minWidth: '50px',
            height: '50px',
            fontSize: '18px',
        },
    },
    clinicianImage: {
        height: '75px',
        width: '75px',
        objectFit: 'contain',
    },
    clinicianInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        width: '100%',
    },
    clinicianTitle: {
        display: 'flex',
        justifyContent: 'flex-start',
        fontWeight: '400',
        fontSize: '16px',
        [theme.breakpoints.down('sm')]: { fontSize: '14px' },
    },
    clinicianSex: {
        display: 'flex',
        justifyContent: 'flex-start',
        fontWeight: '400',
        fontSize: '16px',
        [theme.breakpoints.down('sm')]: { fontSize: '14px' },
        color: '#666666',
    },
}));

function getTimezoneAbbreviation(timezone) {
    const date = new Date();
    const formatted = date.toLocaleString('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
    });

    // Extract the timezone abbreviation from the formatted string
    // Format is typically: "10/10/2025, 3:45:12 PM EDT"
    const match = formatted.match(/\s([A-Z]{3,5})$/);
    return match ? match[1] : timezone;
}

function filterQhpsWithAvailability(qhps) {
    return qhps.filter(qhp => {
        return !Object.values(qhp.availability_by_date).every(date => date.length === 0);
    });
}

export default function ClinicianContainer({
    availabilityByClinician,
    selectedClinician,
    visitTime,
    onVisitTimeSelected,
    timezone,
    forceOpen = false,
}) {
    const classes = useStyles();

    const availabilityByClinicianNoEmptyAvailability = availabilityByClinician
        ? filterQhpsWithAvailability(availabilityByClinician)
        : null;

    return (
        <AxleAccordionContainer
            title='When?'
            subTitle={
                availabilityByClinician && timezone
                    ? `All times in ${getTimezoneAbbreviation(timezone)}`
                    : ''
            }
            icon={<ScheduleIcon fontSize='small' color='primary' />}
            forceOpen={forceOpen}
        >
            {availabilityByClinicianNoEmptyAvailability ? (
                availabilityByClinicianNoEmptyAvailability.length ? (
                    <Box className={classes.clinicianCardWrapper}>
                        {availabilityByClinicianNoEmptyAvailability.map(avail => (
                            <ClinicianCard
                                key={avail.qhp.uuid}
                                clinician={avail.qhp}
                                selectedClinician={selectedClinician}
                                availability={avail.availability_by_date}
                                visitTime={visitTime}
                                onVisitTimeSelected={onVisitTimeSelected}
                                timezone={timezone}
                            />
                        ))}
                    </Box>
                ) : (
                    <div>No availability</div>
                )
            ) : (
                <div>Loading availability...</div>
            )}
        </AxleAccordionContainer>
    );
}

function ClinicianCard({
    clinician,
    selectedClinician,
    availability,
    visitTime,
    onVisitTimeSelected,
    timezone,
}) {
    const classes = useStyles();
    return (
        <Container value={clinician.uuid} className={classes.clinicianCard}>
            <Box className={classes.clinicianCardContent}>
                <div className={classes.clinicianIcon}>
                    {clinician.logo_url ? (
                        <img
                            src={clinician.logo_url}
                            alt={`${clinician.first_name} ${clinician.last_name}`}
                            className={classes.clinicianImage}
                        />
                    ) : (
                        <span>
                            {clinician.first_name.charAt(0)}
                            {clinician.last_name.charAt(0)}
                        </span>
                    )}
                </div>
                <div className={classes.clinicianInfo}>
                    <div className={classes.clinicianTitle}>
                        {clinician.first_name} {clinician.last_name}
                    </div>
                    <div className={classes.clinicianSex}>Sex: {clinician.sex}</div>
                </div>
            </Box>
            <ClinicianTimeSelectionContainer
                clinician={clinician}
                selectedClinician={selectedClinician}
                availability={availability}
                visitTime={visitTime}
                onVisitTimeSelected={onVisitTimeSelected}
                timezone={timezone}
            />
        </Container>
    );
}
